import React, { FC, useMemo, useRef } from "react";
import { useAccordion } from "../hooks/useAccordion";
import { StyledAccordionItem, Content } from "../styled";
import { AccordionTitle } from "./AccordionTitle";

interface Props {
  id?: string;
  title: React.ReactNode;
}

export const AccordionItem: FC<React.PropsWithChildren<Props>> = ({ id, title, children }) => {
  const { activeId, onAccordionClick } = useAccordion();
  const internalId = useRef<string>(getInternalId(title, id));

  if (!internalId.current && process.env.ENV_NAME === "local") {
    console.error(
      `[Accordion] No internal ID found. If you are using a React component for the title, you need to provide an id prop too.`,
    );
  }

  const isOpen = useMemo(() => activeId === internalId.current, [internalId, activeId]);

  return (
    <StyledAccordionItem id={internalId.current}>
      <AccordionTitle
        onClick={() => onAccordionClick(internalId.current)}
        title={title}
        isOpen={isOpen}
      />
      {isOpen && <Content isOpen={isOpen}>{children}</Content>}
    </StyledAccordionItem>
  );
};

function getInternalId(title: React.ReactNode, id?: string) {
  return !!id ? id : isString(title) ? title : "";
}

function isString(title: string | React.ReactNode): title is string {
  return typeof title === "string";
}
